import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import type { RemunerationPreview, YoutillyOverview } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Notice, Spinner, Tile } from '../components/ui.js'
import { addDays, formatDate, formatDateTime, formatTime, todayIso } from '../lib/format.js'
import { PageHead } from './Layout.js'
import { TaskModal } from './TaskDialogs.js'

/**
 * Übergabe: what leaves Hauswerk once the work is done.
 *
 * Left the Vergütungsexport for whoever pays the Mitarbeitenden, right the
 * Rückmeldung to Youtilly, the customer's system. Both only ever contain what
 * was actually done, "endgültig nicht erledigt" never appears here.
 */
export function HandoverPage() {
  return (
    <>
      <PageHead
        title="Übergabe"
        subtitle="Erledigte Leistungen an die Vergütung und an Youtilly weitergeben, jeweils nur, was wirklich erledigt wurde."
      />
      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', alignItems: 'start' }}>
        <Remuneration />
        <Youtilly />
      </div>
    </>
  )
}

function monthRange(today: string, offset: number) {
  const [year, month] = today.split('-').map(Number) as [number, number]
  const first = new Date(Date.UTC(year, month - 1 + offset, 1))
  const last = new Date(Date.UTC(year, month + offset, 0))
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) }
}

function Remuneration() {
  const today = todayIso()
  const lastMonth = monthRange(today, -1)
  const [from, setFrom] = useState(lastMonth.from)
  const [to, setTo] = useState(lastMonth.to)
  const [includeExported, setIncludeExported] = useState(false)
  const query = new URLSearchParams({ from, to, includeExported: String(includeExported) })

  const preview = useQuery({
    queryKey: ['remuneration', query.toString()],
    queryFn: () => api<RemunerationPreview>(`/api/handover/remuneration?${query}`),
    enabled: to >= from,
    placeholderData: (previous) => previous,
  })

  return (
    <section className="card">
      <div className="card-head">
        <h2>Vergütungsexport</h2>
        <span className="muted small">CSV für das Abrechnungssystem</span>
      </div>
      <div className="card-body">
        <div className="row" style={{ marginBottom: 10 }}>
          {[
            { label: 'Letzter Monat', ...monthRange(today, -1) },
            { label: 'Dieser Monat', from: monthRange(today, 0).from, to: today },
            { label: 'Letzte 7 Tage', from: addDays(today, -6), to: today },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="btn secondary small"
              style={{ flex: '0 0 auto' }}
              onClick={() => {
                setFrom(preset.from)
                setTo(preset.to)
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="row">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="von" />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="bis" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0 16px' }}>
          <input type="checkbox" style={{ width: 18, height: 18 }} checked={includeExported} onChange={(e) => setIncludeExported(e.target.checked)} />
          Bereits exportierte Zeilen erneut aufnehmen
        </label>

        {preview.isLoading ? (
          <Spinner />
        ) : preview.data ? (
          <>
            <div className="tiles" style={{ marginBottom: 14 }}>
              <Tile label="Zeilen" value={preview.data.total} sub={`${formatDate(from)} bis ${formatDate(to)}`} />
              <Tile label="Mitarbeitende" value={preview.data.byPerson.length} />
            </div>
            {preview.data.byPerson.length > 0 && (
              <table className="data" style={{ marginBottom: 14 }}>
                <tbody>
                  {preview.data.byPerson.map((row) => (
                    <tr key={row.name}>
                      <td>{row.name}</td>
                      <td className="nowrap" style={{ textAlign: 'right' }}>
                        {row.n} Leistungen
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="row">
              <a className={`btn ${preview.data.total > 0 ? '' : 'secondary'}`} href={`/api/handover/remuneration?${query}&format=csv&markExported=true`} aria-disabled={preview.data.total === 0}>
                <Icon name="handover" /> Exportieren und als übergeben markieren
              </a>
              <a className="btn secondary" href={`/api/handover/remuneration?${query}&format=csv`}>
                <Icon name="download" /> Nur herunterladen
              </a>
            </div>
            <p className="small muted" style={{ marginBottom: 0 }}>
              Spalten: Datum, Uhrzeit, Mitarbeiter, Kunde, Liegenschaft, Objekt, Leistung, Textbaustein, Youtilly-ID, Erfassung. Das
              Zielsystem für die Vergütung ist noch offen; das Format lässt sich anpassen.
            </p>
          </>
        ) : null}
      </div>
    </section>
  )
}

function Youtilly() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [state, setState] = useState<'pending' | 'failed' | 'reported'>('pending')
  const [openTask, setOpenTask] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['youtilly', state],
    queryFn: () => api<YoutillyOverview>(`/api/handover/youtilly?state=${state}`),
    placeholderData: (previous) => previous,
  })

  const report = useMutation({
    mutationFn: (ids?: string[]) => api<{ attempted: number; reported: number; failed: number }>('/api/handover/youtilly/report', { method: 'POST', body: { ids } }),
    onSuccess: async (result) => {
      toast(
        result.failed > 0
          ? `${result.reported} gemeldet, ${result.failed} fehlgeschlagen, siehe Fehlerliste.`
          : `${result.reported} Erledigungen an Youtilly gemeldet.`,
        result.failed > 0 ? 'warn' : undefined,
      )
      await queryClient.invalidateQueries({ queryKey: ['youtilly'] })
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : 'Meldung fehlgeschlagen.', 'error'),
  })

  const counts = overview.data?.counts ?? { pending: 0, failed: 0, reported: 0 }
  const tasks = overview.data?.tasks ?? []

  return (
    <section className="card">
      <div className="card-head">
        <h2>Rückmeldung an Youtilly</h2>
        {overview.data?.simulated && (
          <span className="badge warn" title="Bis der Zugang steht, wird die Schnittstelle simuliert.">
            Simulation
          </span>
        )}
      </div>
      <div className="card-body">
        <Notice kind="info">
          Erledigte Aufgaben mit Youtilly-ID werden 1:1 an das System des Kunden gemeldet. Was Youtilly ablehnt, landet in der
          Fehlerliste und kann korrigiert und erneut gesendet werden, nichts geht still verloren.
        </Notice>
        <div className="segmented" role="group" style={{ marginBottom: 14 }}>
          {(
            [
              ['pending', 'Ausstehend', counts.pending],
              ['failed', 'Fehler', counts.failed],
              ['reported', 'Gemeldet', counts.reported],
            ] as const
          ).map(([value, label, n]) => (
            <button key={value} type="button" aria-pressed={state === value} onClick={() => setState(value)}>
              {label} ({n})
            </button>
          ))}
        </div>

        {state === 'pending' && counts.pending > 0 && (
          <button type="button" className="btn" style={{ marginBottom: 14 }} disabled={report.isPending} onClick={() => report.mutate(undefined)}>
            <Icon name="send" /> {report.isPending ? 'Wird gemeldet …' : `${counts.pending} Erledigungen jetzt melden`}
          </button>
        )}
        {state === 'failed' && counts.failed > 0 && (
          <button type="button" className="btn secondary" style={{ marginBottom: 14 }} disabled={report.isPending} onClick={() => report.mutate(tasks.map((t) => t.id))}>
            <Icon name="repeat" /> Fehlgeschlagene erneut senden
          </button>
        )}

        {overview.isLoading ? (
          <Spinner />
        ) : tasks.length === 0 ? (
          <EmptyState
            title={state === 'pending' ? 'Nichts ausstehend' : state === 'failed' ? 'Keine Fehler' : 'Noch nichts gemeldet'}
            hint={state === 'pending' ? 'Alle erledigten Aufgaben mit Youtilly-ID sind gemeldet.' : undefined}
          />
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Youtilly-ID</th>
                  <th>Leistung</th>
                  <th className="hide-narrow">Wo</th>
                  <th className="nowrap">Erledigt</th>
                  <th>{state === 'failed' ? 'Fehler' : state === 'reported' ? 'Gemeldet' : 'Von'}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id} className="clickable" onClick={() => setOpenTask(task.id)}>
                    <td className="nowrap strong">{task.externalRef}</td>
                    <td>
                      {task.title}
                      {task.textOption && <div className="muted small">{task.textOption}</div>}
                    </td>
                    <td className="small hide-narrow">
                      {task.siteName}
                      {task.path.length > 0 && <div className="muted">{task.path.join(' › ')}</div>}
                    </td>
                    <td className="nowrap small">
                      {formatDate(task.dueDate)}
                      {task.completedAtClient && <div className="muted">{formatTime(task.completedAtClient)} Uhr</div>}
                    </td>
                    <td className="small">
                      {state === 'failed' ? (
                        <span className="badge missed">{task.reportError}</span>
                      ) : state === 'reported' ? (
                        formatDateTime(task.reportedAt)
                      ) : (
                        task.performedByName ?? '–'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <TaskModal
        taskId={openTask}
        onClose={() => setOpenTask(null)}
        onChanged={async (message) => {
          if (message) toast(message)
          setOpenTask(null)
          await queryClient.invalidateQueries({ queryKey: ['youtilly'] })
        }}
      />
    </section>
  )
}
